import React, { useState, useEffect, useRef } from "react";
import { ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTasks } from "../hooks/queries";
import { Task } from "../api/api";
import { taskTarget } from "../lib/swipe";

function timeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diff = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diff < 60) return "Zojuist";
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  return `${Math.floor(diff / 86400)} dag${Math.floor(diff / 86400) === 1 ? "" : "en"} geleden`;
}

export default function TasksBell() {
  const navigate = useNavigate();
  const { data } = useTasks();
  // Every task type is grouped server-side (at most one Task per type), so
  // tasks.length doubles as both the badge count and the row count — no
  // per-item keys/grouping needed here, task.type is already unique.
  const tasks: Task[] = data?.tasks ?? [];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  async function handleTaskClick(task: Task) {
    setOpen(false);
    const target = await taskTarget(task);
    if (target) navigate(target.to, { state: target.state });
  }

  return (
    <div className="relative" ref={ref}>
      <button
        data-tour="tasks-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-11 w-11 items-center justify-center rounded-pill border border-waves-line bg-white text-waves-ink shadow-soft transition-colors hover:bg-waves-mist"
        aria-label="Taken"
      >
        <ListChecks size={19} />
        {tasks.length > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-pill border-2 border-white bg-waves-koraal-hover px-1 text-xs font-bold leading-none text-white">
            {tasks.length > 9 ? "9+" : tasks.length}
          </span>
        )}
      </button>

      {open && (
        <div data-tour="tasks-drop" className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-waves-line bg-white shadow-pop md:w-96">
          <div className="flex items-center justify-between border-b border-waves-line px-5 py-4">
            <h3 className="text-base font-black tracking-tight text-waves-ink">Taken</h3>
            {tasks.length > 0 && (
              <span className="rounded-pill bg-status-alert-bg px-2.5 py-0.5 text-xs font-bold text-status-alert-fg">
                {tasks.length} open
              </span>
            )}
          </div>

          <div className="max-h-[60vh] md:max-h-80 overflow-y-auto">
            {tasks.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-waves-muted">
                Geen openstaande taken
              </div>
            ) : (
              <ul className="divide-y divide-waves-line">
                {tasks.map((task) => (
                  <li
                    key={task.type}
                    onClick={() => handleTaskClick(task)}
                    className="flex cursor-pointer items-start gap-3 px-5 py-3.5 transition-colors hover:bg-waves-subtle"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-snug text-waves-ink">{task.message}</p>
                      <p className="mt-0.5 font-mono text-xs text-waves-muted">{timeAgo(task.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end border-t border-waves-line px-5 py-3">
            <button
              onClick={() => { setOpen(false); navigate("/taken"); }}
              className="text-[13px] font-bold text-waves-ink hover:text-waves-accent"
            >
              Bekijk alle →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
